import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaPause,
  FaPlay,
  FaTrophy,
} from "react-icons/fa";

const SLIDES = [
  {
    src: "/img/insitu/003.jpg",
    alt: "Utilisation de MonAppliTournoi lors d’un tournoi interclubs",
    clubName: "Judo Club d'Eauze",
    location: "Eauze",
    logo: "https://api.judo-club-eauze.fr/uploads/photos_settings/1/logo_club-2026-02-18T205652488Z-400.webp",
    description:
      "Gestion des pesées, répartition des compétiteurs, suivi des combats et résultats publics en temps réel.",
  },
  {
    src: "/img/insitu/002.jpg",
    alt: "Table d’arbitrage utilisant MonAppliTournoi",
    clubName: "Judo Club d'Eauze",
    location: "Eauze",
    logo: "https://api.judo-club-eauze.fr/uploads/photos_settings/1/logo_club-2026-02-18T205652488Z-400.webp",
    description:
      "Une interface pensée pour les tables d’arbitrage, les organisateurs et les coachs.",
  },
  {
    src: "/img/insitu/005.png",
    alt: "Affichage public des résultats pendant un tournoi",
    clubName: "Judo Club Lavernose",
    location: "Lavernose",
    logo: "https://www.judoclublavernose.com/uploaded/152629/img-20240505-wa0005_5.jpg",
    description:
      "Affichage des résultats, scoreboards et informations utiles pour le public.",
  },
  {
    src: "/img/insitu/006.png",
    alt: "Table Centrale",
    clubName: "Judo Club Lavernose",
    location: "Lavernose",
    logo: "https://www.judoclublavernose.com/uploaded/152629/img-20240505-wa0005_5.jpg",
    description:
      "Sortie des poules possibles sur impression si nécessaire, ou affichage sur écran pour le public.",
  },
  {
    src: "/img/insitu/008.jpg",
    alt: "Championnat Départemental FSGT",
    clubName: "FSGT Nord",
    location: "Nord",
    logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRJ4c4T8hH61_0S48Xsd4S_FwHFPC-QQE34KIWlAXJGB9-S8z0Z_cKwa40&s=10",
    description:
      "Championnat Départemental, Régional et National FSGT.",
  },
];

const clampIndex = (value, length) => {
  if (!length) return 0;
  return ((value % length) + length) % length;
};

const InSituClubsSlider = ({
  slides = SLIDES,
  autoPlay = true,
  interval = 6000,
}) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const total = slides.length;
  const current = slides[index];

  const progressPercent = useMemo(() => {
    if (!total) return 0;
    return ((index + 1) / total) * 100;
  }, [index, total]);

  const goTo = useCallback(
    (nextIndex) => {
      setLoaded(false);
      setIndex(clampIndex(nextIndex, total));
    },
    [total]
  );

  const next = useCallback(() => {
    goTo(index + 1);
  }, [goTo, index]);

  const prev = useCallback(() => {
    goTo(index - 1);
  }, [goTo, index]);

  useEffect(() => {
    if (!autoPlay || paused || total <= 1) return undefined;

    const timer = setInterval(() => {
      setLoaded(false);
      setIndex((currentIndex) => clampIndex(currentIndex + 1, total));
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, paused, interval, total]);

  useEffect(() => {
    if (!total) return;

    const nextSlide = slides[clampIndex(index + 1, total)];
    if (!nextSlide?.src) return;

    const img = new Image();
    img.src = nextSlide.src;
  }, [index, slides, total]);

  if (!total || !current) return null;

  return (
    <section className="relative">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />

      <div
        className="relative mx-auto max-w-[1180px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-900"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="grid lg:grid-cols-[1fr_360px]">
          <div className="relative bg-slate-950 p-2 sm:p-3">
            <div className="relative h-[260px] overflow-hidden rounded-[1.5rem] bg-slate-900 sm:h-[360px] lg:h-[520px]">
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/15 border-t-orange-400" />
                </div>
              )}

              <img
                key={current.src}
                src={current.src}
                alt={current.alt}
                loading={index === 0 ? "eager" : "lazy"}
                onLoad={() => setLoaded(true)}
                className={`h-full w-full object-cover object-center transition duration-700 ${
                  loaded ? "scale-100 opacity-100" : "scale-[1.02] opacity-0"
                }`}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-slate-950/10" />

              <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur">
                <FaTrophy className="h-3.5 w-3.5 text-orange-300" />
                En situation réelle
              </div>

              {total > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur transition hover:-translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                    aria-label="Photo précédente"
                  >
                    <FaChevronLeft className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={next}
                    className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur transition hover:translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                    aria-label="Photo suivante"
                  >
                    <FaChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          <aside className="flex flex-col justify-between border-t border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03] sm:p-6 lg:border-l lg:border-t-0">
            <div>
              <div className="mb-5 flex items-center gap-4">
                {current.logo ? (
                  <img
                    src={current.logo}
                    alt={`Logo ${current.clubName}`}
                    loading="lazy"
                    className="h-16 w-16 rounded-2xl bg-white object-contain p-2 shadow-sm ring-1 ring-slate-200 dark:bg-white dark:ring-white/10"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/20">
                    <FaTrophy className="h-6 w-6" />
                  </div>
                )}

                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300">
                    Club utilisateur
                  </p>

                  <h3 className="mt-1 text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                    {current.clubName}
                  </h3>
                </div>
              </div>

              {current.location && (
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-bold text-slate-600 shadow-sm dark:bg-slate-950/50 dark:text-slate-300">
                  <FaMapMarkerAlt className="h-3.5 w-3.5 text-orange-500" />
                  {current.location}
                </div>
              )}

              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {current.description}
              </p>
            </div>

            <div className="mt-6">
              <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-600 to-orange-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setPaused((value) => !value)}
                  className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
                >
                  {paused ? (
                    <>
                      <FaPlay className="h-3.5 w-3.5" />
                      Lecture
                    </>
                  ) : (
                    <>
                      <FaPause className="h-3.5 w-3.5" />
                      Pause
                    </>
                  )}
                </button>

                {total > 1 && (
                  <div className="flex items-center gap-2">
                    {slides.map((slide, slideIndex) => (
                      <button
                        key={slide.src}
                        type="button"
                        onClick={() => goTo(slideIndex)}
                        aria-label={`Voir ${slide.clubName}`}
                        className={`h-2.5 rounded-full transition-all ${
                          slideIndex === index
                            ? "w-8 bg-orange-500"
                            : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-white/20 dark:hover:bg-white/40"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default InSituClubsSlider;